import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { ClipboardList, Plus, Package, Check, X, Truck, Ban, ChevronDown, ChevronUp, Search, Building, Clock } from 'lucide-react';
import { getOrders, createOrder, receiveOrder, closeOrder, cancelOrder, getProducts, getSuppliers, getUserStructures } from '../api';
import { useStructure } from '../contexts/StructureContext';

const OrderManagement = ({ showAllStructures = false }) => {
  const { currentStructure, structures } = useStructure();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [expandedOrder, setExpandedOrder] = useState(null);

  // All user structures (for fetching all orders)
  const [allStructures, setAllStructures] = useState([]);

  // Create order state
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedStructureIds, setSelectedStructureIds] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [newOrder, setNewOrder] = useState({
    supplierId: '',
    notes: '',
    items: [{ productId: '', quantities: {}, unitPrice: '' }] // quantities: { structureId: qty }
  });

  // Receive order state
  const [receivingOrderId, setReceivingOrderId] = useState(null);
  const [receiveData, setReceiveData] = useState({});

  // Product search state
  const [productSearch, setProductSearch] = useState({});

  // Load all user structures on mount
  useEffect(() => {
    const loadStructures = async () => {
      try {
        const res = await getUserStructures();
        setAllStructures(res.data);
      } catch (err) {
        console.error('Error loading structures:', err);
      }
    };
    loadStructures();
  }, []);

  const fetchOrders = useCallback(async () => {
    if (!currentStructure) return;
    try {
      setLoading(true);
      let structureIds;
      if (showAllStructures && allStructures.length > 0) {
        structureIds = allStructures.map(s => s.id);
      } else {
        structureIds = [currentStructure.id];
      }
      const response = await getOrders(structureIds, filterStatus);
      setOrders(response.data);
      setError(null);
    } catch (err) {
      setError('Erreur lors du chargement des commandes');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [allStructures, currentStructure, filterStatus, showAllStructures]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const loadFormData = useCallback(async () => {
    if (!currentStructure) return;
    try {
      const [suppliersRes, productsRes] = await Promise.all([
        getSuppliers(currentStructure.id),
        getProducts(currentStructure.id)
      ]);
      setSuppliers(suppliersRes.data.filter(s => s.active !== false));
      setProducts(productsRes.data);
      // Pre-select current structure
      if (selectedStructureIds.length === 0) {
        setSelectedStructureIds([currentStructure.id]);
      }
    } catch (err) {
      console.error('Error loading form data:', err);
    }
  }, [currentStructure, selectedStructureIds.length]);

  useEffect(() => {
    if (showCreateForm) loadFormData();
  }, [showCreateForm, loadFormData]);

  // Filter products for search
  const getFilteredProducts = (index) => {
    const search = (productSearch[index] || '').toLowerCase();
    if (!search) return products.slice(0, 20);
    return products.filter(p => p.name.toLowerCase().includes(search)).slice(0, 20);
  };

  const handleCreateOrder = async (e) => {
    e.preventDefault();
    if (!newOrder.supplierId) {
      setError('Sélectionnez un fournisseur');
      return;
    }
    if (selectedStructureIds.length === 0) {
      setError('Sélectionnez au moins une structure');
      return;
    }

    // Build items array with structureId per item
    const allItems = [];
    newOrder.items.forEach(item => {
      if (!item.productId || !item.unitPrice) return;
      selectedStructureIds.forEach(sid => {
        const qty = parseInt(item.quantities[sid]) || 0;
        if (qty > 0) {
          allItems.push({
            productId: item.productId,
            quantity: qty,
            unitPrice: item.unitPrice,
            structureId: sid
          });
        }
      });
    });

    if (allItems.length === 0) {
      setError('Ajoutez au moins un produit avec une quantité > 0');
      return;
    }

    try {
      setLoading(true);
      await createOrder({
        supplierId: newOrder.supplierId,
        notes: newOrder.notes,
        items: allItems
      });
      setShowCreateForm(false);
      setNewOrder({ supplierId: '', notes: '', items: [{ productId: '', quantities: {}, unitPrice: '' }] });
      setProductSearch({});
      setSelectedStructureIds([currentStructure.id]);
      fetchOrders();
      setError(null);
    } catch (err) {
      setError('Erreur lors de la création de la commande');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const addItem = () => {
    setNewOrder(prev => ({
      ...prev,
      items: [...prev.items, { productId: '', quantities: {}, unitPrice: '' }]
    }));
  };

  const removeItem = (index) => {
    if (newOrder.items.length <= 1) return;
    setNewOrder(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
    setProductSearch(prev => {
      const updated = { ...prev };
      delete updated[index];
      return updated;
    });
  };

  const updateItem = (index, field, value) => {
    setNewOrder(prev => ({
      ...prev,
      items: prev.items.map((item, i) => i === index ? { ...item, [field]: value } : item)
    }));
  };

  const updateItemQty = (index, structureId, qty) => {
    setNewOrder(prev => ({
      ...prev,
      items: prev.items.map((item, i) => {
        if (i !== index) return item;
        return { ...item, quantities: { ...item.quantities, [structureId]: qty } };
      })
    }));
  };

  const toggleStructure = (structureId) => {
    setSelectedStructureIds(prev =>
      prev.includes(structureId)
        ? prev.filter(id => id !== structureId)
        : [...prev, structureId]
    );
  };

  const selectProduct = (index, product) => {
    updateItem(index, 'productId', product.id);
    // Auto-fill price if product has a current price
    if (product.currentPrice) {
      updateItem(index, 'unitPrice', product.currentPrice);
    }
    setProductSearch(prev => ({ ...prev, [index]: product.name }));
  };

  const startReceive = (order) => {
    setReceivingOrderId(order.id);
    const data = {};
    order.items.forEach(item => {
      // Default to remaining qty (ordered - already received)
      const alreadyReceived = item.receivedQty || 0;
      const remaining = item.quantity - alreadyReceived;
      data[item.id] = remaining;
    });
    setReceiveData(data);
  };

  const handleReceive = async (order) => {
    try {
      setLoading(true);
      await receiveOrder(order.id, {
        receivedBy: currentStructure?.currentUser || 'Admin',
        items: Object.entries(receiveData).map(([orderItemId, receivedQty]) => ({
          orderItemId,
          receivedQty: parseInt(receivedQty)
        }))
      });
      setReceivingOrderId(null);
      setReceiveData({});
      fetchOrders();
      setError(null);
    } catch (err) {
      setError('Erreur lors de la réception');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (orderId) => {
    if (!window.confirm('Annuler cette commande ?')) return;
    try {
      setLoading(true);
      await cancelOrder(orderId);
      fetchOrders();
    } catch (err) {
      setError('Erreur lors de l\'annulation');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = async (orderId) => {
    if (!window.confirm('Clôturer cette commande ? Les articles non reçus ne seront pas ajoutés au stock.')) return;
    try {
      setLoading(true);
      await closeOrder(orderId);
      fetchOrders();
    } catch (err) {
      setError('Erreur lors de la clôture');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      partially_received: 'bg-orange-100 text-orange-800',
      received: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    const labels = {
      pending: 'En attente',
      partially_received: 'Partiellement reçue',
      received: 'Reçue',
      cancelled: 'Annulée'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-800'}`}>
        {labels[status] || status}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric', month: 'short', year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  const getOrderTotal = (order) => {
    return order.items.reduce((sum, item) => sum + item.quantity * parseFloat(item.unitPrice), 0);
  };

  const isMulti = showAllStructures && structures.length >= 2;

  // Groupement par structure pour le mode multi
  const ordersGroupedByStructure = isMulti
    ? orders.reduce((acc, order) => {
        const name = order.structure?.name || 'Structure inconnue';
        if (!acc[name]) acc[name] = [];
        acc[name].push(order);
        return acc;
      }, {})
    : null;
  const sortedStructureNames = ordersGroupedByStructure
    ? Object.keys(ordersGroupedByStructure).sort((a, b) => a.localeCompare(b))
    : [];

  const renderOrderCard = (order) => (
    <Card key={order.id} className="overflow-hidden">
      <div
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition"
        onClick={() => setExpandedOrder(expandedOrder === order.id ? null : order.id)}
      >
        <div className="flex items-center gap-4 flex-1 min-w-0">
          <div className="flex-shrink-0">
            {order.status === 'pending' && <Truck size={20} className="text-yellow-600" />}
            {order.status === 'partially_received' && <Clock size={20} className="text-orange-600" />}
            {order.status === 'received' && <Check size={20} className="text-green-600" />}
            {order.status === 'cancelled' && <Ban size={20} className="text-red-400" />}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-gray-800 truncate">{order.supplier?.name}</span>
              {getStatusBadge(order.status)}
            </div>
            <div className="text-sm text-gray-500">
              {formatDate(order.orderedAt)} &middot; {order.structure?.name} &middot; {order.items.length} article{order.items.length > 1 ? 's' : ''} &middot; {formatCurrency(getOrderTotal(order))}
              {order.orderRef && <span className="ml-1 text-xs text-primary">({order.orderRef})</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {order.status === 'pending' && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); startReceive(order); }}
                className="px-3 py-1.5 bg-green-600 text-white rounded-md hover:bg-green-700 transition text-xs font-medium"
                title="Réceptionner"
              >
                Réceptionner
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); handleCancel(order.id); }}
                className="px-3 py-1.5 bg-gray-200 text-gray-600 rounded-md hover:bg-gray-300 transition text-xs"
                title="Annuler"
              >
                Annuler
              </button>
            </>
          )}
          {order.status === 'partially_received' && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); startReceive(order); }}
                className="px-3 py-1.5 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition text-xs font-medium"
                title="Réceptionner la suite"
              >
                Réceptionner (suite)
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); handleClose(order.id); }}
                className="px-3 py-1.5 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition text-xs font-medium"
                title="Clôturer sans recevoir le reste"
              >
                Clôturer
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); handleCancel(order.id); }}
                className="px-3 py-1.5 bg-gray-200 text-gray-600 rounded-md hover:bg-gray-300 transition text-xs"
                title="Annuler"
              >
                Annuler
              </button>
            </>
          )}
          {expandedOrder === order.id ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </div>
      </div>
      {expandedOrder === order.id && (
        <div className="border-t bg-gray-50 p-4">
          {order.notes && (
            <p className="text-sm text-gray-600 mb-3 italic">Note : {order.notes}</p>
          )}
          {order.receivedAt && (
            <p className="text-sm text-gray-500 mb-3">
              Reçue le {formatDate(order.receivedAt)}{order.receivedBy ? ` par ${order.receivedBy}` : ''}
            </p>
          )}
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b">
                <th className="pb-2">Produit</th>
                <th className="pb-2 text-center">Commandé</th>
                {(order.status === 'received' || order.status === 'partially_received') && <th className="pb-2 text-center">Reçu</th>}
                <th className="pb-2 text-right">Prix unit.</th>
                <th className="pb-2 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {order.items.map(item => {
                const received = item.receivedQty || 0;
                const isPartial = order.status === 'partially_received' || order.status === 'received';
                const isItemComplete = received >= item.quantity;
                return (
                  <tr key={item.id} className={`border-b last:border-0 ${order.status === 'partially_received' && isItemComplete ? 'bg-green-50' : ''}`}>
                    <td className="py-2">{item.product?.name || '—'}</td>
                    <td className="py-2 text-center">{item.quantity}</td>
                    {isPartial && (
                      <td className="py-2 text-center">
                        <span className={`${isItemComplete ? 'text-green-600' : 'text-orange-600'} font-medium`}>
                          {received}/{item.quantity}
                        </span>
                      </td>
                    )}
                    <td className="py-2 text-right">{formatCurrency(item.unitPrice)}</td>
                    <td className="py-2 text-right font-medium">{formatCurrency(item.quantity * parseFloat(item.unitPrice))}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="font-semibold">
                <td className="pt-2" colSpan={(order.status === 'received' || order.status === 'partially_received') ? 3 : 2}></td>
                <td className="pt-2 text-right">Total</td>
                <td className="pt-2 text-right">{formatCurrency(getOrderTotal(order))}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
      {receivingOrderId === order.id && (
        <div className={`border-t p-4 ${order.status === 'partially_received' ? 'bg-orange-50' : 'bg-green-50'}`}>
          <h3 className={`font-medium mb-3 flex items-center gap-2 ${order.status === 'partially_received' ? 'text-orange-800' : 'text-green-800'}`}>
            <Package size={18} />
            {order.status === 'partially_received' ? 'Suite de la réception' : 'Réception de la commande'}
          </h3>
          <p className="text-sm text-gray-600 mb-3">
            {order.status === 'partially_received'
              ? 'Indiquez les quantités reçues pour cette livraison. Les articles déjà complets sont grisés.'
              : 'Vérifiez les quantités reçues. Modifiez si la livraison diffère de la commande.'}
          </p>
          <div className="space-y-2 mb-4">
            {order.items.map(item => {
              const alreadyReceived = item.receivedQty || 0;
              const remaining = item.quantity - alreadyReceived;
              const isItemComplete = alreadyReceived >= item.quantity;
              return (
                <div
                  key={item.id}
                  className={`flex items-center gap-3 p-2 rounded-md ${isItemComplete ? 'bg-gray-100 opacity-60' : 'bg-white'}`}
                >
                  <span className="flex-1 text-sm">
                    {item.product?.name}
                    {isItemComplete && <span className="ml-2 text-green-600 text-xs font-medium">Complet</span>}
                  </span>
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    {alreadyReceived > 0 && (
                      <span className="text-orange-600 font-medium">
                        Déjà reçu : {alreadyReceived}/{item.quantity}
                      </span>
                    )}
                    {!isItemComplete && (
                      <span>Reste : {remaining}</span>
                    )}
                  </div>
                  {!isItemComplete && (
                    <div className="flex items-center gap-1">
                      <label className="text-xs text-gray-500">Reçu :</label>
                      <input
                        type="number"
                        min="0"
                        max={remaining}
                        className={`w-16 px-2 py-1 border rounded-md text-sm text-center focus:outline-none focus:ring-2 focus:ring-green-500 ${
                          parseInt(receiveData[item.id] || 0) !== remaining ? 'border-orange-400 bg-orange-50' : 'border-gray-300'
                        }`}
                        value={receiveData[item.id] || 0}
                        onChange={(e) => setReceiveData(prev => ({ ...prev, [item.id]: e.target.value }))}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => { setReceivingOrderId(null); setReceiveData({}); }}
              className="px-3 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition text-sm"
            >
              Annuler
            </button>
            <button
              onClick={() => handleReceive(order)}
              disabled={loading}
              className={`px-4 py-2 text-white rounded-md transition text-sm disabled:opacity-50 flex items-center gap-2 ${
                order.status === 'partially_received' ? 'bg-orange-500 hover:bg-orange-600' : 'bg-green-600 hover:bg-green-700'
              }`}
            >
              <Check size={16} />
              Valider la réception
            </button>
          </div>
        </div>
      )}
    </Card>
  );

  if (!currentStructure) {
    return (
      <div className="p-8 text-center text-gray-500">
        <ClipboardList size={48} className="mx-auto mb-4 text-gray-300" />
        <p>Sélectionnez une structure pour voir les commandes</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4">
      <div className="space-y-4">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          {error}
          <button onClick={() => setError(null)} className="float-right text-red-500 hover:text-red-700">
            <X size={16} />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <ClipboardList size={22} />
            Commandes
          </h2>
          <select
            className="text-sm border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="">Toutes</option>
            <option value="pending">En attente</option>
            <option value="partially_received">Partiellement reçues</option>
            <option value="received">Reçues</option>
            <option value="cancelled">Annulées</option>
          </select>
        </div>
      </div>

      {/* Create Order Form */}
      {showCreateForm && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Nouvelle commande</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCreateOrder} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Fournisseur *</label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    value={newOrder.supplierId}
                    onChange={(e) => setNewOrder(prev => ({ ...prev, supplierId: e.target.value }))}
                    required
                  >
                    <option value="">Sélectionner...</option>
                    {suppliers.map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    value={newOrder.notes}
                    onChange={(e) => setNewOrder(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Référence, commentaire..."
                  />
                </div>
              </div>

              {/* Structure selection */}
              {structures.length > 1 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Structures *</label>
                  <div className="flex flex-wrap gap-3">
                    {structures.map(s => (
                      <label key={s.id} className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedStructureIds.includes(s.id)}
                          onChange={() => toggleStructure(s.id)}
                          className="rounded w-4 h-4"
                        />
                        <span className={selectedStructureIds.includes(s.id) ? 'font-medium text-gray-800' : 'text-gray-500'}>
                          {s.name}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Items */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Produits *</label>
                <div className="space-y-2">
                  {newOrder.items.map((item, index) => (
                    <div key={index} className="p-3 bg-gray-50 rounded-md space-y-2">
                      <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-end">
                        <div className="flex-1 min-w-0 relative">
                          <label className="block text-xs text-gray-500 mb-1">Produit</label>
                          <input
                            type="text"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                            value={productSearch[index] || ''}
                            onChange={(e) => {
                              setProductSearch(prev => ({ ...prev, [index]: e.target.value }));
                              if (!e.target.value) updateItem(index, 'productId', '');
                            }}
                            placeholder="Rechercher un produit..."
                          />
                          {productSearch[index] && !item.productId && (
                            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-40 overflow-y-auto">
                              {getFilteredProducts(index).map(p => (
                                <button
                                  key={p.id}
                                  type="button"
                                  className="w-full text-left px-3 py-2 text-sm hover:bg-primary/5 transition"
                                  onClick={() => selectProduct(index, p)}
                                >
                                  {p.name}
                                  <span className="text-gray-400 ml-2">Stock: {p.currentStock}</span>
                                </button>
                              ))}
                              {getFilteredProducts(index).length === 0 && (
                                <p className="px-3 py-2 text-sm text-gray-400">Aucun produit trouvé</p>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="w-28">
                          <label className="block text-xs text-gray-500 mb-1">Prix unit.</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                            value={item.unitPrice}
                            onChange={(e) => updateItem(index, 'unitPrice', e.target.value)}
                            placeholder="0.00"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => removeItem(index)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-md transition"
                          title="Retirer"
                        >
                          <X size={18} />
                        </button>
                      </div>
                      {/* Quantities per structure */}
                      {item.productId && (
                        <div className="flex flex-wrap gap-3 ml-1">
                          {selectedStructureIds.map(sid => {
                            const structName = structures.find(s => s.id === sid)?.name || sid;
                            return (
                              <div key={sid} className="flex items-center gap-1">
                                <label className="text-xs text-gray-600 whitespace-nowrap">{structName} :</label>
                                <input
                                  type="number"
                                  min="0"
                                  className="w-16 px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-sm text-center"
                                  value={item.quantities[sid] || ''}
                                  onChange={(e) => updateItemQty(index, sid, e.target.value)}
                                  placeholder="0"
                                />
                              </div>
                            );
                          })}
                          {selectedStructureIds.length > 1 && (
                            <span className="text-xs text-gray-400 self-center">
                              Total : {selectedStructureIds.reduce((sum, sid) => sum + (parseInt(item.quantities[sid]) || 0), 0)}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={addItem}
                  className="mt-2 flex items-center gap-1 text-sm text-primary hover:text-primary-dark transition"
                >
                  <Plus size={16} /> Ajouter un produit
                </button>
              </div>

              {/* Total */}
              {newOrder.items.some(i => i.unitPrice && Object.values(i.quantities).some(q => parseInt(q) > 0)) && (
                <div className="text-right text-sm font-medium text-gray-700">
                  Total estimé : {formatCurrency(
                    newOrder.items.reduce((sum, i) => {
                      const totalQty = selectedStructureIds.reduce((s, sid) => s + (parseInt(i.quantities[sid]) || 0), 0);
                      return sum + (parseFloat(i.unitPrice) || 0) * totalQty;
                    }, 0)
                  )}
                </div>
              )}

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateForm(false);
                    setNewOrder({ supplierId: '', notes: '', items: [{ productId: '', quantities: {}, unitPrice: '' }] });
                    setProductSearch({});
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition text-sm"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-accent text-white rounded-md hover:bg-accent-dark transition text-sm disabled:opacity-50"
                >
                  Enregistrer la commande
                </button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Orders List */}
      {loading && orders.length === 0 ? (
        <p className="text-center text-gray-500 py-8">Chargement...</p>
      ) : orders.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <ClipboardList size={48} className="mx-auto mb-4 text-gray-300" />
          <p>Aucune commande{filterStatus ? ` avec le statut "${filterStatus}"` : ''}</p>
        </div>
      ) : isMulti && ordersGroupedByStructure ? (
        <div className="space-y-4">
          {sortedStructureNames.map(structName => (
            <div key={structName}>
              <div className="flex items-center gap-2 px-4 py-3 bg-primary/10 rounded-t-lg border-l-4 border-primary">
                <Building size={18} className="text-primary" />
                <span className="font-bold text-gray-900">{structName}</span>
                <span className="text-sm text-gray-500">
                  ({ordersGroupedByStructure[structName].length} commande{ordersGroupedByStructure[structName].length !== 1 ? 's' : ''})
                </span>
              </div>
              <div className="bg-gray-50 rounded-b-lg p-3 space-y-2">
                {ordersGroupedByStructure[structName].map(order => renderOrderCard(order))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          {orders.map(order => renderOrderCard(order))}
        </div>
      )}

      </div>

      {/* Floating Action Button - Nouvelle commande */}
      {!showCreateForm && (
        <button
          onClick={() => setShowCreateForm(true)}
          className="fixed bottom-6 right-6 z-30 bg-accent hover:bg-accent-dark text-white rounded-full w-14 h-14 flex items-center justify-center shadow-lg hover:shadow-xl transition-all active:scale-95"
          title="Nouvelle commande"
        >
          <Plus size={28} />
        </button>
      )}
    </div>
  );
};

export default OrderManagement;
